/**
 * NotificationCenter — Bell icon with activity feed.
 * 
 * Desktop: popover with event list grouped by Today/Yesterday/Earlier.
 * Mobile: tapping bell navigates to full-screen activity timeline.
 * 
 * Unread indicator: calm red dot (no count) on both platforms.
 * Read state persisted in localStorage so it survives reloads.
 * 
 * Uses shared activityFeedUtils for identical semantics with ActivityTimeline.
 */

import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Bell, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLivePulse, type PulseEvent } from "@/hooks/useLivePulse";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { formatDistanceToNow, isToday, isYesterday } from "date-fns";
import { ka } from "date-fns/locale";
import { getDisplayLabel, getEventIndicator } from "@/lib/activityFeedUtils";
import { supabase } from "@/integrations/supabase/client";

import { NOTIFICATION_LAST_SEEN_KEY } from "@/lib/uiConstants";

const STORAGE_KEY = "notification-sound-enabled";
const MAX_VISIBLE = 25;

type DayGroup = "today" | "yesterday" | "earlier";

function getStoredSoundPref(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function getStoredLastSeenTs(): number {
  try {
    return parseInt(localStorage.getItem(NOTIFICATION_LAST_SEEN_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function persistLastSeenTs(ts: number) {
  try {
    localStorage.setItem(NOTIFICATION_LAST_SEEN_KEY, String(ts));
  } catch {}
}

function groupByDay(events: PulseEvent[]): { group: DayGroup; events: PulseEvent[] }[] {
  const buckets: Record<DayGroup, PulseEvent[]> = { today: [], yesterday: [], earlier: [] };
  
  for (const event of events) {
    if (isToday(event.timestamp)) {
      buckets.today.push(event);
    } else if (isYesterday(event.timestamp)) {
      buckets.yesterday.push(event);
    } else {
      buckets.earlier.push(event);
    }
  }

  const result: { group: DayGroup; events: PulseEvent[] }[] = [];
  if (buckets.today.length) result.push({ group: "today", events: buckets.today });
  if (buckets.yesterday.length) result.push({ group: "yesterday", events: buckets.yesterday });
  if (buckets.earlier.length) result.push({ group: "earlier", events: buckets.earlier });
  return result;
}

export function NotificationCenter() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [soundEnabled, setSoundEnabled] = useState(getStoredSoundPref);
  const { events } = useLivePulse({ enableSound: soundEnabled });

  const [open, setOpen] = useState(false);
  const lastSeenRef = useRef<number>(getStoredLastSeenTs());
  const [lastSeenTs, setLastSeenTs] = useState<number>(getStoredLastSeenTs);

  // Auto-mark-read on first-ever load (lastSeenTs=0) to prevent false red dot
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && getStoredLastSeenTs() === 0 && events.length > 0) {
      initializedRef.current = true;
      const now = Date.now();
      lastSeenRef.current = now;
      setLastSeenTs(now);
      persistLastSeenTs(now);
    }
  }, [events]);

  const hasUnread = useMemo(
    () => events.some((e) => e.timestamp.getTime() > lastSeenTs),
    [events, lastSeenTs],
  );

  const markRead = useCallback(() => {
    const now = Date.now();
    lastSeenRef.current = now;
    setLastSeenTs(now);
    persistLastSeenTs(now);
  }, []);

  const handleBellClick = useCallback(() => {
    if (isMobile) {
      navigate("/orders?scope=activity");
      markRead();
    }
  }, [isMobile, navigate, markRead]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (isMobile) return;
    setOpen(nextOpen);
    if (nextOpen) {
      markRead();
    }
  }, [isMobile, markRead]);

  const handleToggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {}
      return next;
    });
  }, []);

  const handleEventClick = useCallback(
    async (event: PulseEvent) => {
      setOpen(false);
      try {
        await queryClient.fetchQuery({
          queryKey: ["order", event.orderId],
          queryFn: async () => {
            const { data } = await supabase.from("orders").select("*").eq("id", event.orderId).maybeSingle();
            return data;
          },
          staleTime: 30_000,
        });
      } catch {}
      navigate(`/orders/${event.orderId}?from=notifications`);
    },
    [navigate, queryClient],
  );

  const formatTime = useCallback(
    (date: Date) =>
      formatDistanceToNow(date, {
        addSuffix: true,
        locale: language === "ka" ? ka : undefined,
      }),
    [language],
  );

  const groupLabel = (group: DayGroup) => {
    switch (group) {
      case "today": return t("recentActivity.today");
      case "yesterday": return t("recentActivity.yesterday");
      case "earlier": return t("recentActivity.earlier");
    }
  };

  const visibleEvents = useMemo(() => events.slice(0, MAX_VISIBLE), [events]);
  const grouped = useMemo(() => groupByDay(visibleEvents), [visibleEvents]);

  const unreadDot = hasUnread && (
    <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" />
  );

  if (isMobile) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="relative h-11 w-11 text-white/80 hover:text-white hover:bg-white/10"
        aria-label={t("notifications.title")}
        onClick={handleBellClick}
      >
        <Bell className="h-4 w-4" />
        {unreadDot}
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost-utility"
          size="icon"
          className="relative h-8 w-8"
          aria-label={t("notifications.title")}
        >
          <Bell className="h-3.5 w-3.5" />
          {unreadDot}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[calc(100vw-2rem)] max-w-80 p-0 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">{t("notifications.title")}</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleToggleSound}
              className="p-1 rounded-sm text-muted-foreground hover:text-foreground transition-colors"
              aria-label={soundEnabled ? t("notifications.soundOff") : t("notifications.soundOn")}
              title={soundEnabled ? t("notifications.soundOff") : t("notifications.soundOn")}
            >
              {soundEnabled ? (
                <Volume2 className="h-3.5 w-3.5" />
              ) : (
                <VolumeX className="h-3.5 w-3.5" />
              )}
            </button>
            {events.length > 0 && (
              <button
                onClick={markRead}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("notifications.markAllRead")}
              </button>
            )}
          </div>
        </div>

        {/* Event list — grouped by day */}
        <div className="max-h-72 overflow-y-auto">
          {events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="h-5 w-5 mb-2 opacity-40" />
              <span className="text-xs">{t("notifications.empty")}</span>
            </div>
          ) : (
            grouped.map(({ group, events: groupEvents }) => (
              <div key={group}>
                <p className="text-[10px] font-medium text-muted-foreground/50 uppercase tracking-wider px-3 pt-2.5 pb-1">
                  {groupLabel(group)}
                </p>
                {groupEvents.map((event) => {
                  const isUnread = event.timestamp.getTime() > lastSeenTs;
                  const isCriticalUnread = isUnread && event.isCritical;
                  const { icon: EventIcon, colorClass } = getEventIndicator(event.canonicalType);
                  const displayLabel = getDisplayLabel(event.canonicalType, event.message, language);

                  return (
                    <button
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      className={cn(
                        "w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-accent/50",
                        isCriticalUnread && "bg-destructive/[0.08] border-l-2 border-destructive",
                        isUnread && !event.isCritical && "bg-primary/[0.07] border-l-2 border-primary",
                        !isUnread && "border-l-2 border-transparent",
                      )}
                    >
                      <div className="mt-0.5 shrink-0">
                        <EventIcon className={cn("h-4 w-4", colorClass)} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {isUnread && (
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          )}
                          <span className="text-xs font-medium truncate">
                            #{event.orderNumber}
                          </span>
                          <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
                            {formatTime(event.timestamp)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {event.customerName} — {displayLabel}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Rolling window footer */}
        {events.length > 0 && (
          <div className="border-t px-3 py-1.5">
            <p className="text-[10px] text-muted-foreground/40 text-center">
              {t("notifications.rollingWindow")}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
